import React, { useEffect, useState } from "react";
import { NewOrderDetailsContainer } from "./styled";
import BarekamInput from "../../../components/input";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { getMyEveryRecipient } from "../../../../redux/slices/recipients.slice";
import { Recipient } from "../../../shared/models/user/recipient/recipient";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import { BarekamSelect } from "../../../components/select/select";
import {
  BarekamMenuItem,
  NewRecipientMenuItem,
  SelectedBarekamMenuItem,
} from "../../../components/select/styled/orders";
import { OrderModel } from "../../../shared/models/order/order-item.model";
import { Grid, TextField } from "@mui/material";
import monthlyLimitService, {
  MonthlyLimitValidations,
} from "../../../../services/monthly-limits.service";
import BarekamText from "../../../components/text";
import {
  onMultipleChange,
  onOrderNameChange,
  onRecipientChange,
} from "../../../../services/orders.service";
import { OrderPopupCardTitle } from "../styled";
import { DeliveryAddressSelector } from "../delivery-address-selector/devlivery-address-selector";
import { StandardDialog } from "../../../components/dialogs/standard-dialog/standard-dialog";
import EditRecipient from "../../../user/settings/recipients/edit-recipient";
import { getMessage } from "../../../shared/helper-functions";
import {
  getCities,
  getProvinces,
  getRegions,
} from "../../../../redux/slices/static-data.slice";

const NewOrderDetails = (props: NewOrderDetailsProps) => {
  // TODO: refactor

  const { order, isReadonly, validationErrors } = props;
  const { allRecipients } = useAppSelector(
    (state) => state.recipientDataReducer,
  );
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const [popupOpened, setPopupOpened] = React.useState(false);
  const [orderName, setOrderName] = React.useState(order?.name || '');
  const [monthlyLimitValidations, setMonthlyLimitValidations] =
    useState<MonthlyLimitValidations>({});

  const dispatch = useAppDispatch();

  useEffect(
    () => order.recipient && checkMonthlyLimits(order.recipient),
    [order.products, order.recipient],
  );

  function handleRecipientChange(recipientId: number) {
    const recipient = allRecipients.find(
      (recipient) => recipient.recipientId === recipientId,
    );

    if (recipient) {
      onRecipientChange(recipient, dispatch);
    }
  }

  function onNewRecipientPopupClose(recipientData: Recipient | null) {
    if (!!recipientData) {
      dispatch(getMyEveryRecipient()).then((response) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const recipients = response.payload?.allRecipients.recipients || [];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onRecipientChange(
          recipients.find(
            (recipient: any) =>
              recipient.recipientId === recipientData.recipientId,
          ),
          dispatch,
        );
      });
    }
    setPopupOpened(false);
  }

  function onNewRecipientClicked() {
    Promise.allSettled([
      dispatch(getProvinces()),
      dispatch(getCities()),
      dispatch(getRegions()),
    ]).then(() => {
      setPopupOpened(true);
    });
  }

  function checkMonthlyLimits(orderRecipient: Recipient) {
    const monthlyLimit = allRecipients.find(
      (recipient) => recipient.recipientId === orderRecipient.recipientId,
    )?.monthlyLimit;

    if (monthlyLimit) {
      const { overallCount, overallWeight, overallPrice } =
        monthlyLimitService.getOrderOverallCounts(monthlyLimit, order.products);

      setMonthlyLimitValidations({
        exceedsByCount: monthlyLimit?.pieceCountLimitPerRecipient
          ? overallCount > monthlyLimit.pieceCountLimitPerRecipient
          : false,
        exceedsByWeight: monthlyLimit?.weightLimitPerParcel
          ? overallWeight > monthlyLimit.weightLimitPerParcel
          : false,
        exceedsByPrice: monthlyLimit?.currentMonthOrdersPriceSum
          ? overallWeight > monthlyLimit.currentMonthOrdersPriceSum
          : false,
      });
    }
  }

  function orderNameChange(value: string) {
    // TODO: remove red validation
    setOrderName(value);
    onOrderNameChange(value, dispatch);
  }

  return (
    <>
      <NewOrderDetailsContainer>
        <OrderPopupCardTitle>
          <BarekamText variant={"m"} bold>
            <Message
              messageKey={"new_order_details"}
              sectionId={MessageSections.ORDERS}
            />
          </BarekamText>
        </OrderPopupCardTitle>

        <BarekamInput
          id={"order_name"}
          value={orderName}
          placeholder={getMessage(
            "order_name",
            MessageSections.ORDERS,
            messageCache,
            languageId,
          )}
          customInputVariant={"standard"}
          width={"100%"}
          onChange={(event) => orderNameChange(event.target.value)}
          error={validationErrors.hasOwnProperty("order_name")}
          errorMessage={
            validationErrors["order_name"] &&
            getMessage(
              validationErrors["order_name"],
              MessageSections.ORDERS,
              messageCache,
              languageId,
            )
          }
          disabled={isReadonly}
          required
        />

        <BarekamSelect
          variant={"outlined"}
          label={
            <BarekamText variant={"1rem"}>
              <Message
                messageKey={"new_order_recipient"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          }
          value={order.recipient.recipientId}
          type={"standard"}
          fullWidth
          disabled={isReadonly}
          required
        >
          {allRecipients.map((recipient: Recipient) => {
            if (recipient.recipientId === order.recipient?.recipientId) {
              return (
                <SelectedBarekamMenuItem
                  key={recipient.recipientId}
                  value={recipient.recipientId}
                  onClick={() => handleRecipientChange(recipient.recipientId)}
                >
                  {recipient.firstName} {recipient.lastName}
                </SelectedBarekamMenuItem>
              );
            } else {
              return (
                <BarekamMenuItem
                  key={recipient.recipientId}
                  value={recipient.recipientId}
                  onClick={() => handleRecipientChange(recipient.recipientId)}
                >
                  {recipient.firstName} {recipient.lastName}
                </BarekamMenuItem>
              );
            }
          })}

          <NewRecipientMenuItem
            key={"new-recipient-button"}
            onClick={() => onNewRecipientClicked()}
          >
            <BarekamText variant={"xxl"} width={"unset"}>
              +
            </BarekamText>

            <BarekamText variant={"m"} bold>
              <Message
                messageKey={"add_new_recipient"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          </NewRecipientMenuItem>
        </BarekamSelect>

        {monthlyLimitValidations.exceedsByPrice ? (
          <>
            <Grid item xs={6} />
            <Grid item xs={6}>
              <BarekamText variant={"xs"}>
                <Message
                  messageKey={"recipient_exceeds_by_price"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </Grid>
          </>
        ) : (
          <></>
        )}
        {monthlyLimitValidations.exceedsByWeight ? (
          <>
            <Grid item xs={6} />
            <Grid item xs={6}>
              <BarekamText variant={"xs"}>
                <Message
                  messageKey={"recipient_exceeds_by_weight"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </Grid>
          </>
        ) : (
          <></>
        )}
        {monthlyLimitValidations.exceedsByCount ? (
          <>
            <Grid item xs={6} />
            <Grid item xs={6}>
              <BarekamText variant={"xs"}>
                <Message
                  messageKey={"recipient_exceeds_by_count"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </Grid>
          </>
        ) : (
          <></>
        )}

        <DeliveryAddressSelector
          order={order}
          onSelect={(updateData) => onMultipleChange(updateData, dispatch)}
          isNewOrder={true}
        />
      </NewOrderDetailsContainer>

      <StandardDialog
        headerLabel={
          <Message
            messageKey={"new_recipient"}
            sectionId={MessageSections.USER}
          />
        }
        open={popupOpened}
      >
        <EditRecipient
          recipient={
            {
              firstName: "",
              lastName: "",
              phoneNumber: "",
              passportOrIdSerialNumber: "",
              documentExpirationDate: null,
              address: "",
            } as Recipient
          }
          recipientSaved={(recipientId: any) =>
            onNewRecipientPopupClose(recipientId)
          }
          popupClosed={() => onNewRecipientPopupClose(null)}
        />
      </StandardDialog>
    </>
  );
};

export default NewOrderDetails;

interface NewOrderDetailsProps {
  order: OrderModel;
  isReadonly?: boolean;
  validationErrors: { [key: string]: string };
}
